import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail, Phone, Clock, CheckCircle, ArrowLeft, MapPin, ExternalLink, Download, Shield } from 'lucide-react';
import { BspiFooter, BspiHeader } from './bspi/HomeSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ComplaintsProcedurePage = () => {
  const [policyPdf, setPolicyPdf] = useState({ pdf_url: '', pdf_filename: '' });

  useEffect(() => {
    document.title = 'Complaints Procedure | Black Star PI';
    // Fetch complaints policy PDF if available (public endpoint)
    const fetchPolicy = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/complaints-policy`);
        if (res.ok) {
          const data = await res.json();
          setPolicyPdf(data);
        }
      } catch (e) {
        console.log('No policy PDF available');
      }
    };
    fetchPolicy();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Site-wide header (BspiHeader) */}
      <div className="bspi chrome-only">
        <BspiHeader />
      </div>

      {/* Hero Section */}
      <section className="pt-10 pb-10 md:pt-12 md:pb-12 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-wide">
            Complaints <span className="text-[#D4AF37]">Procedure</span>
          </h1>
          <p className="text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
            At Black Star PI, we are committed to providing an exceptional service.
            If something has not met your expectations, we want to know so we can make it right.
          </p>
        </div>
      </section>

      {/* Our Complaint Policy Section */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 border-2 border-[#D4AF37] rounded-lg flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl md:text-3xl text-[#D4AF37] font-light tracking-widest uppercase">
              Our Complaint Policy
            </h2>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Sleek PDF Download Card */}
          <div className="mb-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 via-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl"></div>
            <div className="relative border border-[#D4AF37]/20 rounded-xl p-8 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left side - Icon and text */}
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center border border-[#D4AF37]/30">
                      <FileText className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <Download className="w-3 h-3 text-black" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl text-white font-medium mb-1">Complaints Policy Document</h3>
                    <p className="text-white/50 text-sm">
                      {policyPdf.pdf_url 
                        ? (policyPdf.pdf_filename || 'BlackStarPI-Complaints-Policy.pdf')
                        : 'Download our full complaints handling policy'}
                    </p>
                  </div>
                </div>
                
                {/* Right side - Download button */}
                {policyPdf.pdf_url ? (
                  <a 
                    href={policyPdf.pdf_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 rounded-xl hover:bg-[#B8941E] transition-all duration-300 font-medium shadow-lg shadow-[#D4AF37]/20 hover:shadow-[#D4AF37]/40 hover:scale-105"
                  >
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                    <span className="uppercase tracking-wider text-sm">Download PDF</span>
                  </a>
                ) : (
                  <div className="flex items-center gap-3 bg-white/5 text-white/40 px-8 py-4 rounded-xl border border-white/10 cursor-not-allowed">
                    <Download className="w-5 h-5" />
                    <span className="uppercase tracking-wider text-sm">Coming Soon</span>
                  </div>
                )}
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#D4AF37]/5 rounded-full blur-2xl"></div>
            </div>
          </div>

          {/* Our Commitment */}
          <div className="mb-16">
            <h2 className="text-2xl text-[#D4AF37] font-light mb-6 tracking-wide">Our Commitment</h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Black Star PI Ltd ("Black Star PI") is dedicated to maintaining the highest 
                standards of service and professionalism. We value your feedback and treat all complaints 
                as opportunities to improve our services.
              </p>
              <p>
                This procedure outlines how we handle complaints and ensures fair, transparent, and 
                timely resolution for all our clients and stakeholders.
              </p>
            </div>
          </div>

          {/* Complaints Process Flowchart */}
          <div className="mb-16">
            <h2 className="text-2xl text-[#D4AF37] font-light mb-8 tracking-wide">Complaints Process Flow</h2>
            
            {/* Visual Flowchart */}
            <div className="relative">
              {/* Connection Line - Desktop */}
              <div className="hidden md:block absolute top-[60px] left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-0.5 bg-gradient-to-r from-[#D4AF37]/50 via-[#D4AF37] to-[#D4AF37]/50"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
                {/* Step 1 */}
                <div className="relative flex flex-col items-center text-center group">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4AF37]/20">
                    <span className="text-black font-bold text-lg">1</span>
                  </div>
                  {/* Arrow down on mobile */}
                  <div className="md:hidden w-0.5 h-8 bg-[#D4AF37]/50 my-2"></div>
                  <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-lg w-full hover:border-[#D4AF37]/30 transition-colors">
                    <h4 className="text-[#D4AF37] font-medium mb-2 text-sm uppercase tracking-wider">Raise Complaint</h4>
                    <p className="text-white/50 text-sm">Contact us via email, phone, or in writing</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative flex flex-col items-center text-center group">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4AF37]/20">
                    <span className="text-black font-bold text-lg">2</span>
                  </div>
                  <div className="md:hidden w-0.5 h-8 bg-[#D4AF37]/50 my-2"></div>
                  <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-lg w-full hover:border-[#D4AF37]/30 transition-colors">
                    <h4 className="text-[#D4AF37] font-medium mb-2 text-sm uppercase tracking-wider">Acknowledgement</h4>
                    <p className="text-white/50 text-sm">We confirm receipt within 3 working days</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative flex flex-col items-center text-center group">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4AF37]/20">
                    <span className="text-black font-bold text-lg">3</span>
                  </div>
                  <div className="md:hidden w-0.5 h-8 bg-[#D4AF37]/50 my-2"></div>
                  <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-lg w-full hover:border-[#D4AF37]/30 transition-colors">
                    <h4 className="text-[#D4AF37] font-medium mb-2 text-sm uppercase tracking-wider">Investigation</h4>
                    <p className="text-white/50 text-sm">Thorough review within 15 working days</p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="relative flex flex-col items-center text-center group">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4AF37]/20">
                    <span className="text-black font-bold text-lg">4</span>
                  </div>
                  <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-lg w-full hover:border-[#D4AF37]/30 transition-colors">
                    <h4 className="text-[#D4AF37] font-medium mb-2 text-sm uppercase tracking-wider">Resolution</h4>
                    <p className="text-white/50 text-sm">Final response with findings & remedies</p>
                  </div>
                </div>
              </div>

              {/* Escalation Path */}
              <div className="mt-8 flex justify-center">
                <div className="flex items-center gap-4 px-6 py-3 bg-white/[0.02] border border-white/10 rounded-full">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse"></div>
                  <span className="text-white/50 text-sm">Not satisfied? Escalate to Senior Management or External Redress</span>
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* How to Make a Complaint - Detailed */}
          <div className="mb-16">
            <h2 className="text-2xl text-[#D4AF37] font-light mb-8 tracking-wide">How to Make a Complaint</h2>
            
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-6 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-white/20 transition-colors">
                <div className="flex-shrink-0 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-xl">1</span>
                </div>
                <div>
                  <h3 className="text-xl text-white font-medium mb-3">Initial Contact</h3>
                  <p className="text-white/50 leading-relaxed">
                    Please contact us directly via email or telephone. Many concerns can be resolved 
                    quickly through direct communication. We encourage you to reach out as soon as 
                    possible after the issue arises.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-white/20 transition-colors">
                <div className="flex-shrink-0 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-xl">2</span>
                </div>
                <div>
                  <h3 className="text-xl text-white font-medium mb-3">Formal Written Complaint</h3>
                  <p className="text-white/50 leading-relaxed mb-4">
                    If your concern remains unresolved, please submit a formal written complaint including:
                  </p>
                  <ul className="text-white/50 space-y-2">
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4AF37] mt-1">●</span>
                      <span>Your full name and contact details</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4AF37] mt-1">●</span>
                      <span>A clear description of your complaint</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4AF37] mt-1">●</span>
                      <span>Relevant dates and supporting documentation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#D4AF37] mt-1">●</span>
                      <span>Your desired outcome or resolution</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-white/20 transition-colors">
                <div className="flex-shrink-0 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-xl">3</span>
                </div>
                <div>
                  <h3 className="text-xl text-white font-medium mb-3">Acknowledgement & Investigation</h3>
                  <p className="text-white/50 leading-relaxed">
                    We will acknowledge your complaint within <span className="text-white font-medium">3 working days</span>. 
                    Our team will investigate thoroughly and provide a full response within{' '}
                    <span className="text-white font-medium">15 working days</span>. If additional time is needed, 
                    we will keep you informed of our progress.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-6 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-white/20 transition-colors">
                <div className="flex-shrink-0 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-black font-bold text-xl">4</span>
                </div>
                <div>
                  <h3 className="text-xl text-white font-medium mb-3">Resolution</h3>
                  <p className="text-white/50 leading-relaxed">
                    We will work to resolve your complaint fairly. Our response will include our findings, 
                    actions taken, and proposed remedies. If you remain dissatisfied, you may request 
                    escalation to senior management for further review.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Response Timeframes */}
          <div className="mb-16">
            <div className="p-8 bg-white/[0.02] border border-[#D4AF37]/20 rounded-lg">
              <div className="flex items-start gap-6">
                <Clock className="w-10 h-10 text-[#D4AF37] flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-xl text-white font-medium mb-6">Response Timeframes</h3>
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="text-center md:text-left">
                      <p className="text-3xl text-[#D4AF37] font-light mb-1">3</p>
                      <p className="text-white/80 text-sm uppercase tracking-wider">Working Days</p>
                      <p className="text-white/40 text-sm mt-1">Acknowledgement</p>
                    </div>
                    <div className="text-center md:text-left">
                      <p className="text-3xl text-[#D4AF37] font-light mb-1">15</p>
                      <p className="text-white/80 text-sm uppercase tracking-wider">Working Days</p>
                      <p className="text-white/40 text-sm mt-1">Initial Response</p>
                    </div>
                    <div className="text-center md:text-left">
                      <p className="text-3xl text-[#D4AF37] font-light mb-1">8</p>
                      <p className="text-white/80 text-sm uppercase tracking-wider">Weeks Maximum</p>
                      <p className="text-white/40 text-sm mt-1">Final Resolution</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Our Complaints Department */}
          <div className="mb-16">
            <h2 className="text-2xl text-[#D4AF37] font-light mb-8 tracking-wide">Contact Our Complaints Department</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="mailto:complaints@black-starpi.com"
                className="flex items-center gap-4 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-[#D4AF37]/50 transition-all group"
              >
                <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Mail className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-medium">Email</p>
                  <p className="text-white/50 text-sm">complaints@black-starpi.com</p>
                </div>
              </a>
              
              <a 
                href="tel:+441313811989"
                className="flex items-center gap-4 p-6 bg-white/[0.02] border border-white/10 rounded-lg hover:border-[#D4AF37]/50 transition-all group"
              >
                <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Phone className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-medium">Telephone</p>
                  <p className="text-white/50 text-sm">+44 131 381 1989</p>
                </div>
              </a>
            </div>
          </div>

          {/* External Redress - PIMS */}
          <div className="mb-16">
            <h2 className="text-2xl text-[#D4AF37] font-light mb-6 tracking-wide">External Redress</h2>
            <div className="p-8 bg-white/[0.02] border border-white/10 rounded-lg">
              <p className="text-white/60 leading-relaxed mb-6">
                Black Star PI Ltd is a member of the Property Investors Mediation Service (PIMS),
                an independent scheme for property-related disputes.
                If you remain dissatisfied after our internal complaints procedure, or if eight weeks
                have passed since you first raised your complaint with us, you may refer the matter
                to PIMS for review.
              </p>

              <div className="mb-6 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-md text-sm text-white/70">
                <span className="uppercase tracking-widest text-[10px] text-[#D4AF37] block mb-1">PIMS Membership Number</span>
                <span data-testid="pims-membership-number" className="font-mono text-white/90">
                  133801
                </span>
              </div>
              
              <a 
                href="https://www.thepims.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-6 py-4 rounded-lg hover:bg-[#B8941E] transition-colors font-medium"
                data-testid="pims-external-link"
              >
                <ExternalLink className="w-5 h-5" />
                Visit PIMS (Property Investors Mediation Service)
                <span className="text-black/60 text-sm ml-2">thepims.co.uk</span>
              </a>
              
              <p className="text-white/40 text-sm mt-4">
                You have twelve months from our final response to refer your complaint to PIMS.
              </p>
            </div>
          </div>

          {/* Our Promise */}
          <div className="p-8 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg">
            <div className="flex items-start gap-6">
              <CheckCircle className="w-10 h-10 text-[#D4AF37] flex-shrink-0" />
              <div>
                <h3 className="text-xl text-white font-medium mb-4">Our Promise to You</h3>
                <p className="text-white/50 leading-relaxed">
                  We treat all complaints with the utmost seriousness and confidentiality. Every complaint 
                  is an opportunity to improve our services. We are committed to handling your concerns 
                  with fairness, transparency, and professionalism. Your feedback helps us maintain the 
                  exceptional standards that our clients expect from Black Star PI.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer — shared v2 footer for consistency with the homepage */}
      <div className="bspi chrome-only">
        <BspiFooter />
      </div>
    </div>
  );
};
